import { ClientPropertyContextProvider, PropertyTab } from '../contexts'
import { FC, useEffect, useMemo, useRef } from 'react'
import { useGetPropertyAssignments, useGetPropertyWorkspace } from 'dataQueries'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { BKBNThemeWrapper } from 'components/common/BKBNThemeWrapper'
import { ClientPropertyPageLayout } from '../controllers/ClientPropertyPageLayout.controller'
import { DealDTOClient } from 'models/deal'
import NotFound from 'components/pages/General/NotFound/NotFound'
import { PageTransition } from 'utils/animations'
import { Path } from 'constants/router'
import { getPropertyAssignmentsByTab } from 'utils/helpers/getPropertyAssignmentsByTab'
import { getPropertyAssignmentsTab, resolvePropertyWorkspaceId } from 'utils/helpers'
import { useUserData } from 'components/contexts/UserDataContext'

export interface OutletProps {
  propertyId: string
  tab: string
  assignmentId: string
  propertyAssignments: DealDTOClient
}

export const ClientProperty: FC = () => {
  const { id, tab, assignmentId } = useParams<{ id: string; tab: PropertyTab; assignmentId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const hasNavigatedRef = useRef<boolean>(false)
  const {
    userPropertyWorkspaceId,
    setCurrentUserWorkspace,
    getWorkspaceItemById
  } = useUserData()


  const derivedTab = useMemo(() => {
    if (tab) return tab
    const regex = new RegExp(`property/${id}/([^/]+)`)

    const match = pathname.match(regex)

    return match ? match[1] as PropertyTab : PropertyTab.GALLERY
  }, [id, tab, pathname])

  const propertyId = useMemo(() => id, [id])
  const workspaceId = useMemo(() => userPropertyWorkspaceId, [userPropertyWorkspaceId])

  const getPropertyWorkspace = useGetPropertyWorkspace(propertyId ?? '')
  // Scope property fetches to the property's own workspace, falling back to the global selection,
  // so a property living in another of the user's workspaces does not 403. See ISSUE-2217.
  const resolvedWorkspaceId = useMemo(
    () => resolvePropertyWorkspaceId(getPropertyWorkspace.isFetched, getPropertyWorkspace.data?.data.workspaceId, workspaceId),
    [getPropertyWorkspace.isFetched, getPropertyWorkspace.data?.data.workspaceId, workspaceId]
  )

  const getPropertyAssignments = useGetPropertyAssignments(propertyId ?? '', resolvedWorkspaceId)
  const propertyAssignmentsData = useMemo(() => getPropertyAssignments.data?.data ?? null, [getPropertyAssignments.data])
  const filteredPropertyAssignmentsDataByTab = useMemo(() => getPropertyAssignmentsByTab(derivedTab, propertyAssignmentsData), [propertyAssignmentsData, derivedTab])
  const firstAssignmentId = filteredPropertyAssignmentsDataByTab[0]?.id
  const firstAssignmentTab = useMemo(() => getPropertyAssignmentsTab(filteredPropertyAssignmentsDataByTab[0]), [filteredPropertyAssignmentsDataByTab])

  const navigateToAssignment = (assignmentId: string, tab: string) => {
    navigate(Path.PROPERTY_TAB_ASSIGNMENT.replace(':id', propertyId!).replace(':tab', tab).replace(':assignmentId', assignmentId))
  }

  const navigateToTabOnly = (tab: string) => {
    navigate(Path.PROPERTY_TAB.replace(':id', propertyId!).replace(':tab', tab))
  }

  // Check property workspace and update selected workspace if needed
  useEffect(() => {
    const propertyWorkspaceId = getPropertyWorkspace.data?.data.workspaceId
    if (propertyWorkspaceId && workspaceId && propertyWorkspaceId !== workspaceId) {
      sessionStorage.setItem('workspaceId', propertyWorkspaceId)
      const workspace = getWorkspaceItemById(propertyWorkspaceId)
      if (workspace) setCurrentUserWorkspace(workspace)
    }
  }, [getPropertyWorkspace.data?.data.workspaceId, getWorkspaceItemById, setCurrentUserWorkspace, workspaceId])

  useEffect(() => {
    if (hasNavigatedRef.current) return

    if (derivedTab === PropertyTab.AI_TEXT_GENERATOR || derivedTab === PropertyTab.MARKETING_CAMPAIGN || derivedTab === PropertyTab.ORDERS || derivedTab === PropertyTab.INFORMATION) return

    if (!assignmentId && firstAssignmentId) navigateToAssignment(firstAssignmentId, firstAssignmentTab || derivedTab)

    if (!assignmentId && !firstAssignmentId) navigateToTabOnly(derivedTab)

    // Mainly for guarding against misput tab/assignmentId in the url. (e.g. email link)
    // First check if there's a valid assignmentId in the url, if not, redirect to the first available assignmentId
    // If the assignmentId is valid, then redirect to the valid tab for that assignmentId
    if (assignmentId) {
      const isAssignmentExist = propertyAssignmentsData?.some((assignment) => assignment.id.toString() === assignmentId)

      if (!isAssignmentExist && firstAssignmentId) {
        navigateToAssignment(firstAssignmentId, firstAssignmentTab || derivedTab)
      } else {
        const existingAssignmentData = propertyAssignmentsData?.filter((assignment) => assignment.id.toString() === assignmentId)
        const existAssignmentTab = getPropertyAssignmentsTab(existingAssignmentData?.[0])

        navigateToAssignment(assignmentId, existAssignmentTab || derivedTab)
      }
    }

    // Set this to true to avoid infinite loop navigation when assignment exists
    if (propertyAssignmentsData) {
      hasNavigatedRef.current = true
    }
    // exclude navigateToAssignment(), navigateToTabOnly(), derivedTab, firstAssignmentTab from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, firstAssignmentId, propertyId, propertyAssignmentsData])

  if (!propertyId) return <NotFound />

  return (
    <BKBNThemeWrapper>
      <ClientPropertyContextProvider propertyId={propertyId} resolvedWorkspaceId={resolvedWorkspaceId} isPropertyWorkspaceResolved={getPropertyWorkspace.isFetched} tab={derivedTab} assignmentId={assignmentId ?? firstAssignmentId} propertyAssignmentsData={propertyAssignmentsData}>
        <PageTransition>
          <ClientPropertyPageLayout />
        </PageTransition>
      </ClientPropertyContextProvider>
    </BKBNThemeWrapper>
  )
}
